import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Calendar, Clock, Users, Search, Check, X, ChevronLeft, ChevronRight, History, Minus } from 'lucide-react';
import DetailLayout from './layouts/DetailLayout';
import { Button, Card, CardContent, CardHeader, CardTitle, Badge, Input } from './ui';
import { ChevronDown, ChevronUp, Activity } from 'lucide-react';
import PlayerAvatar from './ui/PlayerAvatar';
import ProgramaAsignadoCard from './training/ProgramaAsignadoCard';
import ResumenRecorrido from './training/ResumenRecorrido';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useAuth } from '../hooks/useAuth';
import {
  getGroupById,
  getGroupPlayers,
  getSesionById,
  getOrCreateSesionEntrenamiento,
  getAsistenciasBySesion,
  getPrevSesionConAsistencia,
  getSesionConAsistenciaByFecha,
  getSesionPreferidaPorFecha,
  saveAttendance
} from '../lib/supabase';
import { updateSesion } from '../lib/supabase';
import { isActiveGroupRelation, isVisiblePlayer } from '../lib/playerVisibility';

const STATUS_LABELS = {
  programado: 'Programado',
  en_curso: 'En curso',
  terminado: 'Terminado',
  cancelado: 'Cancelado',
  pospuesto: 'Pospuesto'
};

const STATUS_COLORS = {
  programado: 'bg-blue-50 text-blue-700 border-blue-200',
  en_curso: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  terminado: 'bg-slate-50 text-slate-700 border-slate-200',
  cancelado: 'bg-rose-50 text-rose-700 border-rose-200',
  pospuesto: 'bg-amber-50 text-amber-700 border-amber-200'
};

const AttendanceScreen = () => {
  const { groupId, eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { coach } = useAuth();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();

  const [group, setGroup] = useState(location.state?.group || null);
  const [sesion, setSesion] = useState(null);
  const [recorridoOpen, setRecorridoOpen] = useState(false);
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [ignoreEventId, setIgnoreEventId] = useState(false);
  const requestIdRef = useRef(0);

  const getLocalDateValue = useCallback((value = new Date()) => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(value);
  }, []);

  const [fecha, setFecha] = useState(() => {
    const stateFecha = location.state?.fecha;
    if (stateFecha) return stateFecha;
    return getLocalDateValue();
  });

  const loadTrainingData = useCallback(async () => {
    if (!groupId) return;
    const requestId = ++requestIdRef.current;
    const isLatest = () => requestIdRef.current === requestId;
    try {
      if (!isLatest()) return;
      setLoading(true);
      setAttendance({});

      if (!group) {
        const { data, error } = await getGroupById(groupId);
        if (error) throw error;
        if (isLatest()) setGroup(data);
      }

      let sessionData = null;
      const forceFecha = Boolean(location.state?.forceFecha);
      const shouldIgnoreEventId = ignoreEventId || forceFecha;
      if (eventId && !shouldIgnoreEventId) {
        const { data, error } = await getSesionById(eventId);
        if (error) throw error;
        sessionData = data;
      } else {
        const { data: sesionPreferida, error: sesionPreferidaError } =
          await getSesionPreferidaPorFecha({
            groupId,
            fecha
          });
        if (sesionPreferidaError) throw sesionPreferidaError;
        if (sesionPreferida?.id) {
          const { data, error } = await getSesionById(sesionPreferida.id);
          if (error) throw error;
          sessionData = data;
        } else {
          const { data: sesionConAsistencia, error: sesionAsistenciaError } =
            await getSesionConAsistenciaByFecha({
              groupId,
              fecha
            });
          if (sesionAsistenciaError) throw sesionAsistenciaError;
          if (sesionConAsistencia?.sesion?.id) {
            const { data, error } = await getSesionById(sesionConAsistencia.sesion.id);
            if (error) throw error;
            sessionData = data;
          }
        }
      }

      if (!sessionData) {
        const { data, error } = await getOrCreateSesionEntrenamiento({
          groupId,
          fecha,
          serieId: location.state?.serieId || null
        });
        if (error) throw error;
        sessionData = data;
      }

      if (!sessionData) {
        throw new Error('No se pudo preparar la sesion de entrenamiento.');
      }

      if (isLatest()) {
        setSesion(sessionData);
        if (ignoreEventId) {
          setIgnoreEventId(false);
        }
        if (sessionData.fecha && sessionData.fecha !== fecha) {
          setFecha(sessionData.fecha);
        }
      }

      const { data: playersData, error: playersError } = await getGroupPlayers(groupId);
      if (playersError) throw playersError;

      const processedPlayers = [];
      const seen = new Set();
      (playersData || []).forEach((rel) => {
        const player = rel.jugadores;
        if (!player) return;
        if (!isVisiblePlayer(player)) return;
        if (!isActiveGroupRelation(rel)) return;
        if (!seen.has(player.id)) {
          seen.add(player.id);
          processedPlayers.push(player);
        }
      });
      if (isLatest()) setPlayers(processedPlayers);

      const asistenciaRecords = await getAsistenciasBySesion(sessionData.id);
      const attendanceMap = {};

      (asistenciaRecords || []).forEach((record) => {
        if (!record.jugador_id) return;
        attendanceMap[record.jugador_id] = record.presente;
      });
      if (isLatest()) setAttendance(attendanceMap);
    } catch (error) {
      console.error('Error cargando asistencia de entrenamiento:', error);
      const msg = error?.message || 'No se pudo cargar la asistencia.';
      if (msg === 'La serie asignada está inactiva') {
        if (await confirm('Horario inactivo', 'El horario asignado a este grupo está inactivo. Actívalo o asigna uno activo para continuar.')) {
          navigate('/admin/entrenamientos-regulares');
        }
      } else {
        await alert('Error', msg);
      }
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [groupId, eventId, fecha, group, location.state, alert, confirm, ignoreEventId]);

  useEffect(() => {
    loadTrainingData();
  }, [loadTrainingData]);

  const filteredPlayers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return players;
    return players.filter((player) => {
      return (
        player.nombre_completo?.toLowerCase().includes(term) ||
        player.numero?.toString().includes(term) ||
        player.nickname?.toLowerCase().includes(term)
      );
    });
  }, [players, searchTerm]);

  const stats = useMemo(() => {
    const summary = {
      presente: 0,
      retardo: 0,
      ausente: 0,
      exento: 0,
      pendientes: 0
    };

    players.forEach((player) => {
      const state = attendance[player.id];
      if (!state) {
        summary.pendientes += 1;
      } else if (state === 'exento') {
        summary.exento += 1;
      } else if (summary[state] !== undefined) {
        summary[state] += 1;
      }
    });

    return summary;
  }, [attendance, players]);

  const getNextState = (current) => {
    if (current === 'presente') return 'retardo';
    if (current === 'retardo') return 'ausente';
    if (current === 'ausente') return 'exento';
    if (current === 'exento') return 'presente';
    return 'presente';
  };

  const updateAttendance = async (playerId, nextState) => {
    if (!sesion?.id) return;
    try {
      setSaving(true);
      setAttendance((prev) => ({ ...prev, [playerId]: nextState }));
      const descripcion =
        sesion?.titulo ||
        sesion?.serie_entrenamiento?.nombre ||
        `Entrenamiento ${fecha}`;
      const payload = {
        jugador_id: playerId,
        grupo_id: groupId,
        sesion_id: sesion.id,
        fecha,
        presente: nextState,
        descripcion,
        staff_id: coach?.rol === 'admin' ? null : coach?.id || null
      };
      const { error } = await saveAttendance(payload);
      if (error) throw error;
      if ((sesion?.estatus || 'programado').toLowerCase() === 'programado') {
        const { data, error: statusError } = await updateSesion(sesion.id, { estatus: 'en_curso' });
        if (!statusError && data?.estatus) {
          setSesion((prev) => (prev ? { ...prev, estatus: data.estatus } : prev));
        }
      }
    } catch (error) {
      console.error('Error guardando asistencia:', error);
      await alert('Error', 'No se pudo guardar la asistencia.');
    } finally {
      setSaving(false);
    }
  };

  const handleCycleAttendance = (playerId) => {
    const current = attendance[playerId];
    const nextState = getNextState(current);
    updateAttendance(playerId, nextState);
  };

  const formatFecha = useMemo(() => {
    return new Date(`${fecha}T00:00:00`).toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  }, [fecha]);

  const todayValue = useMemo(() => getLocalDateValue(), [getLocalDateValue]);
  const isToday = fecha === todayValue;
  const canGoNextDay = !isToday;

  const goToFecha = (targetFecha) => {
    const currentPath = `/attendance/${groupId}`;
    const statePayload = {
      group,
      fecha: targetFecha,
      serieId: location.state?.serieId || null,
      forceFecha: true
    };

    if (targetFecha !== fecha) {
      setFecha(targetFecha);
    }

    setIgnoreEventId(true);
    navigate(currentPath, { replace: true, state: statePayload });
  };

  const shiftDate = (delta) => {
    const dateObj = new Date(`${fecha}T00:00:00`);
    dateObj.setDate(dateObj.getDate() + delta);
    const next = getLocalDateValue(dateObj);
    goToFecha(next);
  };

  const statusKey = (sesion?.estatus || 'programado').toLowerCase();
  const isEditable = !['terminado', 'cancelado'].includes(statusKey);

  const markAllPendingAs = async (state) => {
    if (!sesion?.id || saving) return;
    const pending = players.filter((p) => !attendance[p.id]);
    if (!pending.length) { await alert('Info', 'No hay jugadores pendientes.'); return; }
    const label = state === 'presente' ? 'presentes' : 'ausentes';
    if (!await confirm('Marcar asistencia', `Marcar ${pending.length} jugador(es) pendientes como ${label}?`)) return;
    try {
      setSaving(true);
      const descripcion = sesion?.titulo || sesion?.serie_entrenamiento?.nombre || `Entrenamiento ${fecha}`;
      const staffId = coach?.rol === 'admin' ? null : coach?.id || null;
      const payload = pending.map((p) => ({
        jugador_id: p.id, grupo_id: groupId, sesion_id: sesion.id,
        fecha, presente: state, descripcion, staff_id: staffId
      }));
      const { error } = await saveAttendance(payload);
      if (error) throw error;
      const updated = { ...attendance };
      pending.forEach((p) => { updated[p.id] = state; });
      setAttendance(updated);
      if ((sesion?.estatus || 'programado').toLowerCase() === 'programado') {
        const { data } = await updateSesion(sesion.id, { estatus: 'en_curso' });
        if (data?.estatus) setSesion((prev) => prev ? { ...prev, estatus: data.estatus } : prev);
      }
    } catch (error) {
      await alert('Error', 'No se pudo guardar la asistencia.');
    } finally {
      setSaving(false);
    }
  };

  const finishAttendance = async () => {
    if (!sesion || finishing) return;
    const pending = players.filter((p) => !attendance[p.id]);
    const message = pending.length > 0
      ? `Hay ${pending.length} jugador(es) sin marcar que se registrarán como ausentes:\n\n${pending.slice(0, 5).map((p) => `• ${p.nombre_completo}`).join('\n')}${pending.length > 5 ? `\n• ...y ${pending.length - 5} más` : ''}\n\n¿Terminar sesión?`
      : '¿Terminar la sesión? Ya no se podrán hacer cambios (a menos que la reactives).';
    if (!await confirm('Terminar sesión', message, { destructive: pending.length > 0 })) return;
    try {
      setFinishing(true);
      if (pending.length > 0) {
        const descripcion = sesion?.titulo || sesion?.serie_entrenamiento?.nombre || `Entrenamiento ${fecha}`;
        const staffId = coach?.rol === 'admin' ? null : coach?.id || null;
        const payload = pending.map((p) => ({
          jugador_id: p.id, grupo_id: groupId, sesion_id: sesion.id,
          fecha, presente: 'ausente', descripcion, staff_id: staffId
        }));
        await saveAttendance(payload);
        const updated = { ...attendance };
        pending.forEach((p) => { updated[p.id] = 'ausente'; });
        setAttendance(updated);
      }
      const { data, error } = await updateSesion(sesion.id, { estatus: 'terminado' });
      if (error) throw error;
      setSesion((prev) => (prev ? { ...prev, estatus: data.estatus } : prev));
    } catch (error) {
      console.error('Error finalizando asistencia:', error);
      await alert('Error', 'No se pudo terminar la asistencia.');
    } finally {
      setFinishing(false);
    }
  };

  const reactivateAttendance = async () => {
    if (!sesion || finishing) return;
    try {
      setFinishing(true);
      const { data, error } = await updateSesion(sesion.id, { estatus: 'en_curso' });
      if (error) throw error;
      setSesion((prev) => (prev ? { ...prev, estatus: data.estatus } : prev));
    } catch (error) {
      console.error('Error reactivando asistencia:', error);
      await alert('Error', 'No se pudo reactivar la asistencia.');
    } finally {
      setFinishing(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const goToPrevWithAttendance = async () => {
    try {
      const { data, error } = await getPrevSesionConAsistencia({
        groupId,
        beforeDate: fecha
      });
      if (error) throw error;
      if (!data?.sesion?.id) {
        await alert('Sin registros', 'No hay asistencias anteriores registradas.');
        return;
      }
      navigate(`/attendance/${groupId}/${data.sesion.id}`, {
        state: { group, fecha: data.sesion.fecha }
      });
    } catch (error) {
      console.error('Error buscando asistencia previa:', error);
      await alert('Error', 'No se pudo cargar la asistencia anterior.');
    }
  };

  return (
    <DetailLayout
      title="Asistencia de entrenamiento"
      breadcrumbs={[
        { label: 'Grupos', path: '/groups' },
        { label: group?.nombre_del_grupo || 'Grupo', path: `/groups/${groupId}` },
        { label: 'Asistencia de entrenamiento' }
      ]}
      backPath={`/groups/${groupId}`}
    >
      <div className="space-y-3">
        {/* Programa de entrenamiento asignado */}
        {sesion?.id && (
          <ProgramaAsignadoCard sesionId={sesion.id} />
        )}
        {/* Recorrido historico (si la sesion ya termino el programa) */}
        {sesion?.programa_recorrido && (
          <Card>
            <CardHeader
              className="pb-2 pt-3 cursor-pointer"
              onClick={() => setRecorridoOpen((p) => !p)}
            >
              <CardTitle className="text-sm font-semibold flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-slate-500" />
                  Recorrido del entrenamiento
                </span>
                {recorridoOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CardTitle>
            </CardHeader>
            {recorridoOpen && (
              <CardContent className="pt-2 pb-3">
                <ResumenRecorrido recorrido={sesion.programa_recorrido} />
              </CardContent>
            )}
          </Card>
        )}
        {/* Header */}
        <Card>
          <CardHeader className="pb-2 pt-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <CardTitle className="text-lg truncate">{group?.nombre_del_grupo || 'Grupo'}</CardTitle>
                <p className="text-sm text-muted-foreground truncate">
                  {sesion?.serie_entrenamiento?.nombre || 'Sesión de entrenamiento'}
                </p>
              </div>
              <Badge
                variant="outline"
                className={`text-xs uppercase shrink-0 ${STATUS_COLORS[sesion?.estatus || 'programado'] || STATUS_COLORS.programado}`}
              >
                {STATUS_LABELS[sesion?.estatus || 'programado']}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2.5 pb-3">
            {/* Info: fecha + hora + jugadores */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs sm:text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" /> {formatFecha}
              </span>
              {sesion?.hora_inicio && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> {sesion.hora_inicio.slice(0, 5)}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" /> {players.length}
              </span>
            </div>

            {/* Stats — labels cortas en móvil, completas en desktop */}
            <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap">
              <StatPill mobileLabel="P" desktopLabel="Presentes" value={stats.presente} className="bg-emerald-50 text-emerald-700 border border-emerald-200" />
              <StatPill mobileLabel="R" desktopLabel="Retardos" value={stats.retardo} className="bg-amber-50 text-amber-700 border border-amber-200" />
              <StatPill mobileLabel="A" desktopLabel="Ausentes" value={stats.ausente} className="bg-rose-50 text-rose-700 border border-rose-200" />
              {stats.exento > 0 && <StatPill mobileLabel="E" desktopLabel="Exentos" value={stats.exento} className="bg-slate-50 text-slate-600 border border-slate-200" />}
              {stats.pendientes > 0 && <StatPill mobileLabel="?" desktopLabel="Pendientes" value={stats.pendientes} className="bg-gray-50 text-gray-600 border border-gray-200" />}
            </div>

            {/* Acciones masivas — versión móvil */}
            {isEditable && stats.pendientes > 0 && (
              <div className="flex gap-1.5 sm:hidden">
                <Button variant="outline" size="sm" className="h-7 flex-1 text-[11px] px-2" onClick={() => markAllPendingAs('presente')} disabled={saving}>
                  <Check className="h-3 w-3 mr-1 text-emerald-600 shrink-0" />
                  <span className="truncate">Presentes ({stats.pendientes})</span>
                </Button>
                <Button variant="outline" size="sm" className="h-7 flex-1 text-[11px] px-2" onClick={() => markAllPendingAs('ausente')} disabled={saving}>
                  <X className="h-3 w-3 mr-1 text-rose-600 shrink-0" />
                  <span className="truncate">Ausentes ({stats.pendientes})</span>
                </Button>
              </div>
            )}

            {/* Navegación + Terminar — siempre 1 fila */}
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={goToPrevWithAttendance} title="Anterior con asistencia">
                <History className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => shiftDate(-1)} title="Día anterior">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-8 px-2.5 text-xs shrink-0" onClick={() => goToFecha(getLocalDateValue())} disabled={isToday}>
                Hoy
              </Button>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => shiftDate(1)} disabled={!canGoNextDay} title="Día siguiente">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              {/* Buscador — inline en desktop */}
              <div className="relative hidden md:block w-full max-w-[220px] ml-1">
                <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Buscar jugador..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <div className="flex-1" />
              {isEditable && stats.pendientes > 0 && (
                <>
                  <Button variant="outline" size="sm" className="h-8 text-xs shrink-0 hidden sm:inline-flex" onClick={() => markAllPendingAs('presente')} disabled={saving}>
                    <Check className="h-3 w-3 mr-1" /> Todos presentes
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs shrink-0 hidden sm:inline-flex" onClick={() => markAllPendingAs('ausente')} disabled={saving}>
                    <X className="h-3 w-3 mr-1" /> Todos ausentes
                  </Button>
                </>
              )}
              {isEditable ? (
                <Button size="sm" className="h-8 shrink-0" onClick={finishAttendance} disabled={finishing}>
                  <span className="hidden sm:inline">{finishing ? 'Procesando...' : 'Terminar sesión'}</span>
                  <span className="sm:hidden">{finishing ? '...' : 'Terminar'}</span>
                </Button>
              ) : (
                <Button size="sm" variant="secondary" className="h-8 shrink-0" onClick={reactivateAttendance} disabled={finishing}>
                  <span className="hidden sm:inline">Reactivar sesión</span>
                  <span className="sm:hidden">Reactivar</span>
                </Button>
              )}
            </div>

            {/* Buscador — full width en móvil (en desktop vive en la toolbar) */}
            <div className="relative md:hidden">
              <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Buscar jugador..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {/* Jugadores */}
        <Card className="overflow-hidden">
          <CardContent className="pt-4 pb-4 overflow-hidden">
            {loading ? (
              <div className="py-12 text-center text-muted-foreground">Cargando asistencia...</div>
            ) : filteredPlayers.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">No hay jugadores disponibles.</div>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {filteredPlayers.map((player) => {
                  const estado = attendance[player.id] || null;
                  const cardState = estado || 'pendiente';
                  const cardStyle =
                    cardState === 'presente' ? 'ring-2 ring-green-500 bg-green-50/50'
                    : cardState === 'retardo' ? 'ring-2 ring-amber-500 bg-amber-50/50'
                    : cardState === 'ausente' ? 'ring-2 ring-rose-400 bg-rose-50/50'
                    : cardState === 'exento' ? 'ring-2 ring-slate-400 bg-slate-50/50'
                    : 'border hover:bg-muted/50';
                  const statusIcon =
                    cardState === 'presente' ? <Check className="h-3 w-3" />
                    : cardState === 'retardo' ? <Clock className="h-3 w-3" />
                    : cardState === 'ausente' ? <X className="h-3 w-3" />
                    : cardState === 'exento' ? <Minus className="h-3 w-3" />
                    : null;
                  const statusBg =
                    cardState === 'presente' ? 'bg-green-600'
                    : cardState === 'retardo' ? 'bg-amber-500'
                    : cardState === 'ausente' ? 'bg-rose-500'
                    : cardState === 'exento' ? 'bg-slate-500'
                    : 'bg-slate-300';
                  const statusLabel =
                    cardState === 'presente' ? 'Presente'
                    : cardState === 'retardo' ? 'Retardo'
                    : cardState === 'ausente' ? 'Ausente'
                    : cardState === 'exento' ? 'Exento'
                    : 'Pendiente';

                  return (
                    <button
                      key={player.id}
                      type="button"
                      disabled={saving || !isEditable}
                      onClick={() => handleCycleAttendance(player.id)}
                      className={`relative rounded-xl p-2 sm:p-3 text-center transition-all active:scale-[0.97] shadow-sm overflow-hidden min-w-0 ${cardStyle} disabled:opacity-50`}
                    >
                      <div className={`absolute top-1.5 right-1.5 sm:top-2 sm:right-2 ${statusBg} text-white rounded-full p-0.5 sm:p-1`}>
                        {statusIcon || <Users className="h-2.5 w-2.5" />}
                      </div>
                      <PlayerAvatar
                        imagen_url={player.imagen_url}
                        nombre_completo={player.nombre_completo}
                        size="lg"
                      />
                      <p className="text-xs sm:text-sm font-semibold mt-1 sm:mt-1.5 leading-tight truncate">
                        {player.nickname || player.nombre_completo}
                      </p>
                      {player.numero && (
                        <p className="text-[10px] sm:text-[11px] text-muted-foreground">#{player.numero}</p>
                      )}
                      <Badge
                        variant="outline"
                        className={`mt-0.5 sm:mt-1 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0 ${
                          cardState === 'presente' ? 'border-green-300 text-green-700'
                          : cardState === 'retardo' ? 'border-amber-300 text-amber-700'
                          : cardState === 'ausente' ? 'border-rose-300 text-rose-700'
                          : cardState === 'exento' ? 'border-slate-300 text-slate-600'
                          : 'border-dashed text-muted-foreground'
                        }`}
                      >
                        {statusLabel}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog />
    </DetailLayout>
  );
};

const StatPill = ({ label, mobileLabel, desktopLabel, value, className = '' }) => (
  <span className={`inline-flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-[11px] sm:text-xs font-medium ${className}`}>
    {mobileLabel ? (
      <>
        <span className="font-normal sm:hidden">{mobileLabel}</span>
        <span className="font-normal hidden sm:inline">{desktopLabel}</span>
      </>
    ) : (
      <span className="font-normal">{label}</span>
    )}
    <span className="font-bold">{value}</span>
  </span>
);

const StatChip = ({ label, value, tone = 'default' }) => {
  const toneClasses = {
    default: 'bg-slate-50 text-slate-700 border-slate-200',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    danger: 'bg-rose-50 text-rose-700 border-rose-200'
  };

  return (
    <Card className={`border shadow-none ${toneClasses[tone] || toneClasses.default}`}>
      <CardContent className="py-2 px-3 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
};

export default AttendanceScreen;






